"use client";

import { Check, Euro, Home, Users } from "lucide-react";

import ImageSlide from "@/components/shared/ImageSlide";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import type { Apartment } from "@/payload-types";
import type { PayLoadData } from "@/utils/payloadUtil";

import RichTextContent from "../Content/RichTextContent";

type Props = {
  detail: PayLoadData<Apartment> | undefined;
};

export default function DetailApartmentContent({ detail }: Props) {
  const { language } = useLanguage();

  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col items-center gap-36 pb-40">
      <ImageSlide
        key={detail?.[language].id}
        images={detail?.[language].images || []}
      />
      <div className="mx-auto grid grid-cols-3 gap-10 px-5 md:gap-20">
        <div className="flex flex-col items-center gap-5">
          <div className="rounded-full bg-primary-100 p-4 md:p-8">
            <Home className="size-8 text-primary-600" />
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg font-semibold text-gray-900 md:text-xl">
              {detail?.[language].attributes?.area}
            </p>
            <p className="text-gray-500">{t({ en: "Area", de: "Fläche" })}</p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5">
          <div className="rounded-full bg-primary-100 p-4 md:p-8">
            <Users className="size-8 text-primary-600" />
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg font-semibold text-gray-900 md:text-xl">
              {detail?.[language].attributes?.numberOfPeoples}
            </p>
            <p className="text-gray-500">{t({ en: "Guest", de: "Gast" })}</p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5">
          <div className="rounded-full bg-primary-100 p-4 md:p-8">
            <Euro className="size-8 text-primary-600" />
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg font-semibold text-gray-900 md:text-xl">
              {detail?.[language].price}
            </p>
            <p className="text-gray-500">
              {t({ en: "Per night", de: "Pro Nacht" })}
            </p>
          </div>
        </div>
      </div>
      {detail?.[language].description && (
        <div className="container mx-auto max-w-3xl px-5 text-lg font-light leading-10">
          <RichTextContent data={detail[language].description} />
        </div>
      )}
      <ul className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-5">
        <h3 className="mb-4 w-fit border-b border-gray-200 text-3xl font-light md:text-4xl">
          Amenities
        </h3>
        {detail?.[language].amenities?.map((amenity) => (
          <li
            key={amenity.id}
            className="flex gap-2 border-b border-gray-200 pb-2 text-lg font-light"
          >
            {" "}
            <Check className="text-primary-700" /> {amenity.name}
          </li>
        ))}
      </ul>
    </div>
  );
}
